import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
//services
import changePassword from 'services/forgotPassword/changePassword'
import sendForgotEmailService from 'services/forgotPassword/sendEmail'
import { setError } from 'ducks/user'

// Components
import ForgotForm from '../../components/Forms/Forgot'
import Logo from '../../components/UI/Logo'
import ChangePasswordForm from 'views/components/Forms/changePassword'
import { useNotification } from 'lib/context/notification.context'
import { useTranslation } from 'react-i18next'
import Input from 'views/components/UI/TextInput'
import { Button } from 'views/components/UI/Buttons'
import { verifyActionCode } from 'services/auth/action-code'
import { Container as RecoveryCard, LoginDescription, LoginEyebrow } from './styled'

const PASSWORD_RECOVERY_TECHNICAL_ERROR_MESSAGE =
  'No fue posible procesar la solicitud en este momento. Inténtalo nuevamente más tarde o comunícate con el administrador del sistema.'

const RecoveryContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const RecoveryTitle = styled.h1`
  margin: 0;
  color: #183449;
  font-family: 'Public Sans', sans-serif;
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.2;
  text-align: center;
`

const RecoveryDescription = styled(LoginDescription)`
  margin: 0;
`

const RecoveryModeLink = styled.button`
  align-self: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #2d749c;
  font-family: 'Public Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    color: #c6016b;
  }
`

const CodeFields = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const CodeAction = styled.div`
  width: 100%;
`

const ForgotFormContainer: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { getWarning, getSuccess, getError } = useNotification()
  const navigate = useNavigate()
  const paramValue = localStorage.getItem('i18nextLng')

  const [emailSended, setEmailSended] = useState<boolean>(false)
  const [recoveryEmail, setRecoveryEmail] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [isCodeValidated, setCodeValidated] = useState<boolean>(false)
  const [isValidatingCode, setValidatingCode] = useState<boolean>(false)
  const [changePasswordSuccess, setChangePasswordSuccess] = useState<boolean>(false)

  const handleSendEmailForgot = (values: any): void => {
    const email = values.email?.trim().toLowerCase()
    sendForgotEmailService(email, paramValue || 'es')
      .then(() => {
        setRecoveryEmail(email)
        setEmailSended(true)
        getSuccess(t('global.common.forgotPassword.notifications.sendSuccess'))
        dispatch(setError(false))
      })
      .catch((error) => {
        const status = error?.response?.status

        if (status === 400) {
          getError(t('global.notifications.emailNotExist'))
          return
        }

        if (status === 422) {
          getWarning(t('global.notifications.emailNotMain'))
          return
        }

        if (status === 421) {
          getError(t('global.notifications.userNotActive'))
          return
        }

        getError(PASSWORD_RECOVERY_TECHNICAL_ERROR_MESSAGE)
      })
  }

  const handleChangePassword = (pass: string): void => {
    changePassword({
      password: pass,
    })
      .then(() => {
        getSuccess(t('global.common.forgotPassword.notifications.success'))
        setChangePasswordSuccess(true)
        setTimeout(() => {
          navigate('/')
        }, 3000)
      })
      .catch(() => {
        getWarning(t('global.common.forgotPassword.notifications.error'))
      })
  }

  const handleValidateCode = async (): Promise<void> => {
    if (!recoveryEmail || !code.trim()) {
      getWarning('Introduce el código temporal recibido por correo.')
      return
    }

    try {
      setValidatingCode(true)
      await verifyActionCode(recoveryEmail, code.trim(), 'password_recovery')
      setCodeValidated(true)
    } catch (error: any) {
      getWarning(error?.response?.data?.message || 'El código es inválido o ha expirado.')
    } finally {
      setValidatingCode(false)
    }
  }

  return (
    <RecoveryCard>
      <Logo width="245px" />
      <RecoveryContent>
        {!emailSended ? (
          <>
            <LoginEyebrow>Recuperación de acceso</LoginEyebrow>
            <RecoveryTitle>{t('global.common.forgotPassword.title')}</RecoveryTitle>
            <RecoveryDescription>{t('global.common.forgotPassword.description')}</RecoveryDescription>
            <RecoveryModeLink onClick={() => setEmailSended(true)} type="button">
              Ya tengo un código
            </RecoveryModeLink>
            <ForgotForm onSubmit={(values) => handleSendEmailForgot(values)} />
          </>
        ) : !isCodeValidated ? (
          <>
            <LoginEyebrow>Recuperación de acceso</LoginEyebrow>
            <RecoveryTitle>Valida el código temporal</RecoveryTitle>
            <RecoveryDescription>
              {recoveryEmail
                ? `Introduce el código enviado a ${recoveryEmail}.`
                : 'Introduce el correo y el código recibido.'}
            </RecoveryDescription>
            <RecoveryModeLink onClick={() => setEmailSended(false)} type="button">
              Solicitar un nuevo código
            </RecoveryModeLink>
            <CodeFields>
              {!recoveryEmail && (
                <Input
                  value={recoveryEmail}
                  onChange={(event) => setRecoveryEmail(event.target.value.trim().toLowerCase())}
                  type="email"
                  name="recovery-email"
                  label="Correo electrónico"
                />
              )}
              <Input
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                type="text"
                name="recovery-code"
                label="Código temporal"
              />
              <CodeAction>
                <Button fullWidth disabled={isValidatingCode} onClick={handleValidateCode} size="lg" type="button">
                  {isValidatingCode ? 'Validando código...' : 'Validar código'}
                </Button>
              </CodeAction>
            </CodeFields>
          </>
        ) : (
          <>
            {!changePasswordSuccess ? (
              <ChangePasswordForm onSubmit={(e) => handleChangePassword(e.password)} />
            ) : (
              <>
                <LoginEyebrow>Contraseña actualizada</LoginEyebrow>
                <RecoveryTitle>{t('global.common.forgotPassword.changedPassword')}</RecoveryTitle>
                <RecoveryDescription>
                  {t('global.common.forgotPassword.changedPasswordDescription')}
                </RecoveryDescription>
              </>
            )}
          </>
        )}
      </RecoveryContent>
    </RecoveryCard>
  )
}

export default ForgotFormContainer
